/**
 * Hooks React pour les coaching_sessions (Module Seances v2).
 * Pattern useState + useEffect, coherent avec le reste du projet.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  createSession as apiCreate,
  deleteSession as apiDelete,
  duplicateSession as apiDuplicate,
  getErrorMessage,
  getSession as apiGet,
  getSuggestion,
  listSessions,
  listSessionTemplates,
} from '../services/coaching_sessions'

// Note (chantier 5, 2026-05-10) : les hooks d'edition de seance (update,
// save-as-template, add/update/delete/reorder block) ont ete RETIRES.
// L'edition de seance est desormais reservee a Insightball (superadmin
// backend). Cote coach, seuls subsistent : list, get, create-from-template,
// duplicate, delete.


export function useSessionList(filters) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const reqIdRef = useRef(0)

  const fetchList = useCallback(async () => {
    const reqId = ++reqIdRef.current
    setLoading(true)
    setError(null)
    try {
      const fn = filters?.mode === 'templates' ? listSessionTemplates : listSessions
      const result = await fn(filters)
      if (reqId !== reqIdRef.current) return
      setData(result)
    } catch (err) {
      if (reqId !== reqIdRef.current) return
      setError(getErrorMessage(err, 'Impossible de charger les séances.'))
    } finally {
      if (reqId === reqIdRef.current) setLoading(false)
    }
  }, [
    filters?.mode,
    filters?.status,
    filters?.date_from,
    filters?.date_to,
    filters?.is_template,
    filters?.theme,
    filters?.page,
    filters?.size,
  ])

  useEffect(() => { fetchList() }, [fetchList])

  return { data, loading, error, refetch: fetchList }
}


export function useSession(id) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchOne = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      const result = await apiGet(id)
      setData(result)
    } catch (err) {
      setError(getErrorMessage(err, "Impossible de charger la séance."))
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { fetchOne() }, [fetchOne])

  return { data, loading, error, refetch: fetchOne, setData }
}


export function useSessionTemplates({ theme } = {}) {
  return useSessionList({ mode: 'templates', size: 50, theme })
}


export function useSuggestion(season) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchIt = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await getSuggestion({ season })
      setData(result)
    } catch (err) {
      setError(getErrorMessage(err, "Impossible de charger les suggestions."))
    } finally {
      setLoading(false)
    }
  }, [season])

  useEffect(() => { fetchIt() }, [fetchIt])

  return { data, loading, error, refetch: fetchIt }
}


// ─── Mutations ─────────────────────────────────────────────────────

function makeMutation(apiFn) {
  return function useMutation() {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const mutate = useCallback(async (...args) => {
      setLoading(true)
      setError(null)
      try {
        return await apiFn(...args)
      } catch (err) {
        setError(getErrorMessage(err, "Opération impossible."))
        throw err
      } finally {
        setLoading(false)
      }
    }, [])

    return { mutate, loading, error }
  }
}

export const useCreateSession    = makeMutation(apiCreate)
export const useDeleteSession    = makeMutation(apiDelete)
export const useDuplicateSession = makeMutation(apiDuplicate)
